import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Skeleton,
  Stack,
  Textarea,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, createFileRoute, redirect } from "@tanstack/react-router"
import { useState, type ReactNode } from "react"
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"

import PlatformShell from "../../../components/PlatformAdmin/PlatformShell"
import { activatePlatformTenant, archivePlatformTenant, getPlatformTenant, listPlatformTenantUsageHistory, sendPlatformTenantAdminPasswordReset, suspendPlatformTenant } from "../../../client/platform"
import useCustomToast, { getApiErrorMessage } from "../../../hooks/useCustomToast"
import { isPlatformLoggedIn, isTenantSubdomainHost } from "../../../utils/platformAuth"

export const Route = createFileRoute("/platform/spaces/$tenantId")({
  component: PlatformTenantDetailsPage,
  beforeLoad: async () => {
    if (isTenantSubdomainHost()) {
      throw redirect({ to: "/login" })
    }
    if (!isPlatformLoggedIn()) {
      throw redirect({ to: "/platform/login" })
    }
  },
})

function PlatformTenantDetailsPage() {
  const { tenantId } = Route.useParams()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const [reason, setReason] = useState("")
  const tenantQuery = useQuery({
    queryKey: ["platformAdmin", "tenant", tenantId],
    queryFn: () => getPlatformTenant(tenantId),
  })
  const usageHistoryQuery = useQuery({
    queryKey: ["platformAdmin", "tenantUsageHistory", tenantId],
    queryFn: () => listPlatformTenantUsageHistory(tenantId, { days: 30 }),
  })
  const suspendMutation = useMutation({
    mutationFn: () => suspendPlatformTenant(tenantId, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "tenant", tenantId] })
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "tenants"] })
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "auditLogs"] })
      showToast("Tenant suspended", "Space has been suspended.", "success")
    },
    onError: (error: unknown) => {
      showToast("Action failed", getApiErrorMessage(error), "error")
    },
  })
  const activateMutation = useMutation({
    mutationFn: () => activatePlatformTenant(tenantId, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "tenant", tenantId] })
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "tenants"] })
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "auditLogs"] })
      showToast("Tenant activated", "Space has been activated.", "success")
    },
    onError: (error: unknown) => {
      showToast("Action failed", getApiErrorMessage(error), "error")
    },
  })
  const archiveMutation = useMutation({
    mutationFn: () => archivePlatformTenant(tenantId, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "tenant", tenantId] })
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "tenants"] })
      await queryClient.invalidateQueries({ queryKey: ["platformAdmin", "auditLogs"] })
      showToast("Tenant archived", "Space has been archived.", "success")
    },
    onError: (error: unknown) => {
      showToast("Action failed", getApiErrorMessage(error), "error")
    },
  })
  const sendPasswordResetMutation = useMutation({
    mutationFn: () => sendPlatformTenantAdminPasswordReset(tenantId),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["platformAdmin", "tenant", tenantId] })
      void queryClient.invalidateQueries({ queryKey: ["platformAdmin", "auditLogs"] })
      showToast("Reset email sent", `Password reset link sent to ${data.email}.`, "success")
    },
    onError: (error: unknown) => {
      showToast("Action failed", getApiErrorMessage(error), "error")
    },
  })

  return (
    <PlatformShell
      title="Space details"
      description="Podstawowe dane tenantu, runtime health i pierwszy administrator."
    >
      {tenantQuery.isLoading ? (
        <Stack spacing={3}>
          <Skeleton h="32px" />
          <Skeleton h="120px" />
          <Skeleton h="120px" />
        </Stack>
      ) : null}

      {tenantQuery.isError ? (
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertDescription>Failed to load tenant details.</AlertDescription>
        </Alert>
      ) : null}

      {tenantQuery.data ? (
        <Stack spacing={6}>
          <Box>
            <Button
              as={Link}
              to="/platform/spaces"
              variant="ghost"
              size="sm"
              px={0}
            >
              Back to spaces
            </Button>
          </Box>

          <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} gap={4} direction={{ base: "column", md: "row" }}>
            <Box>
              <Heading size="md">{tenantQuery.data.tenant.name || tenantQuery.data.tenant.subdomain}</Heading>
              <Text color="gray.500">{tenantQuery.data.tenant.subdomain}</Text>
            </Box>
            <Stack direction={{ base: "column", md: "row" }} spacing={3}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const targetEmail = tenantQuery.data?.primary_admin?.email || tenantQuery.data?.tenant.initial_admin_email || "primary admin"
                  if (!window.confirm(`Send password reset email to ${targetEmail}?`)) return
                  sendPasswordResetMutation.mutate()
                }}
                isDisabled={!tenantQuery.data.primary_admin}
                isLoading={sendPasswordResetMutation.isPending}
              >
                Send password reset
              </Button>
              <Badge colorScheme={tenantQuery.data.tenant.runtime_healthy ? "green" : "orange"} px={3} py={1} borderRadius="full">
                {tenantQuery.data.tenant.runtime_healthy ? "runtime healthy" : "runtime issue"}
              </Badge>
              {tenantQuery.data.tenant.lifecycle_status === "archived" ? (
                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={() => {
                    if (!window.confirm(`Activate tenant ${tenantQuery.data?.tenant.subdomain}?`)) return
                    activateMutation.mutate()
                  }}
                  isLoading={activateMutation.isPending}
                >
                  Activate
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={() => {
                      if (!window.confirm(`Suspend tenant ${tenantQuery.data?.tenant.subdomain}?`)) return
                      suspendMutation.mutate()
                    }}
                    isLoading={suspendMutation.isPending}
                  >
                    Suspend
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="orange"
                    onClick={() => {
                      if (!window.confirm(`Archive tenant ${tenantQuery.data?.tenant.subdomain}? This will keep the data but block runtime access.`)) return
                      archiveMutation.mutate()
                    }}
                    isLoading={archiveMutation.isPending}
                  >
                    Archive
                  </Button>
                  {tenantQuery.data.tenant.lifecycle_status !== "active" ? (
                    <Button
                      size="sm"
                      colorScheme="green"
                      onClick={() => {
                        if (!window.confirm(`Activate tenant ${tenantQuery.data?.tenant.subdomain}?`)) return
                        activateMutation.mutate()
                      }}
                      isLoading={activateMutation.isPending}
                    >
                      Activate
                    </Button>
                  ) : null}
                </>
              )}
            </Stack>
          </Flex>

          <Box>
            <Text fontSize="sm" color="gray.500" mb={2}>Status reason / operator note</Text>
            <Textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Optional note for audit log and tenant status"
              size="sm"
            />
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            <InfoCard title="General">
              <InfoRow label="Schema" value={tenantQuery.data.tenant.db_schema} />
              <InfoRow label="Lifecycle" value={tenantQuery.data.tenant.lifecycle_status} />
              <InfoRow label="Plan" value={tenantQuery.data.tenant.plan_code || "free"} />
              <InfoRow label="Created" value={new Date(tenantQuery.data.tenant.created_at).toLocaleString()} />
              <InfoRow label="Activated" value={formatMaybeDate(tenantQuery.data.tenant.activated_at)} />
              <InfoRow label="Archived" value={formatMaybeDate(tenantQuery.data.tenant.archived_at)} />
            </InfoCard>

            <InfoCard title="Usage">
              <InfoRow label="Active users" value={String(tenantQuery.data.active_users_count)} />
              <InfoRow label="Total users" value={String(tenantQuery.data.usage_snapshot.total_users_count || 0)} />
              <InfoRow label="Companies" value={String(tenantQuery.data.usage_snapshot.companies_count || 0)} />
              <InfoRow label="Documents" value={String(tenantQuery.data.usage_snapshot.documents_count || 0)} />
              <InfoRow label="Initial admin email" value={tenantQuery.data.tenant.initial_admin_email || ""} />
              <InfoRow label="Last runtime seen" value={formatMaybeDate(tenantQuery.data.tenant.last_runtime_seen_at)} />
              <InfoRow label="Last user activity" value={formatMaybeDate(tenantQuery.data.usage_snapshot.last_user_seen_at)} />
              <InfoRow label="Status reason" value={tenantQuery.data.tenant.status_reason || ""} />
            </InfoCard>
          </SimpleGrid>

          <InfoCard title="Telemetry snapshot">
            <InfoRow label="Files" value={String(tenantQuery.data.usage_snapshot.storage_file_count || 0)} />
            <InfoRow label="Storage" value={formatBytes(tenantQuery.data.usage_snapshot.storage_used_bytes || 0)} />
            <InfoRow label="API calls this month" value={String(tenantQuery.data.usage_snapshot.api_calls_month || 0)} />
            <InfoRow label="Push sent this month" value={String(tenantQuery.data.usage_snapshot.push_sent_month || 0)} />
            <InfoRow label="PDF generated this month" value={String(tenantQuery.data.usage_snapshot.pdf_generated_month || 0)} />
            <InfoRow label="Snapshot taken at" value={formatMaybeDate(tenantQuery.data.usage_snapshot.snapshot_taken_at)} />
          </InfoCard>

          <InfoCard title="Usage trend (30 days)">
            {usageHistoryQuery.isLoading ? (
              <Skeleton h="280px" />
            ) : usageHistoryQuery.isError ? (
              <Text color="red.500">Failed to load usage history.</Text>
            ) : usageHistoryQuery.data && usageHistoryQuery.data.data.length > 0 ? (
              <Box h="280px">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={usageHistoryQuery.data.data.map((entry) => ({
                    date: new Date(entry.snapshot_date).toLocaleDateString(),
                    activeUsers: entry.active_users_count,
                    documents: entry.documents_count,
                    storageMb: Number((entry.storage_used_bytes / (1024 * 1024)).toFixed(2)),
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="activeUsers" stroke="#2b6cb0" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="documents" stroke="#2f855a" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="storageMb" stroke="#dd6b20" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Text color="gray.500">Not enough history yet. The trend will appear as daily snapshots accumulate.</Text>
            )}
          </InfoCard>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
            <InfoCard title="Company">
              {tenantQuery.data.company_logo ? (
                <Image src={tenantQuery.data.company_logo} alt={tenantQuery.data.company_name || "Company logo"} maxH="80px" mb={3} objectFit="contain" />
              ) : null}
              <InfoRow label="Name" value={tenantQuery.data.company_name || "Not set"} />
            </InfoCard>

            <InfoCard title="Primary admin">
              {tenantQuery.data.primary_admin ? (
                <>
                  <InfoRow label="Full name" value={tenantQuery.data.primary_admin.full_name} />
                  <InfoRow label="Email" value={tenantQuery.data.primary_admin.email} />
                  <InfoRow label="Last seen" value={formatMaybeDate(tenantQuery.data.primary_admin.last_seen_at)} />
                </>
              ) : (
                <Text color="gray.500">Primary admin not found in tenant schema, so password reset cannot be sent.</Text>
              )}
            </InfoCard>
          </SimpleGrid>

          {!tenantQuery.data.tenant.runtime_healthy && tenantQuery.data.tenant.runtime_error ? (
            <Alert status="warning" borderRadius="lg">
              <AlertIcon />
              <AlertDescription>{tenantQuery.data.tenant.runtime_error}</AlertDescription>
            </Alert>
          ) : null}

          <InfoCard title="Audit log">
            {tenantQuery.data.audit_logs.length > 0 ? (
              <Stack spacing={3}>
                {tenantQuery.data.audit_logs.map((entry) => (
                  <Box key={entry.id} borderWidth="1px" borderColor="gray.100" borderRadius="md" p={3}>
                    <Flex justify="space-between" gap={3} direction={{ base: "column", md: "row" }}>
                      <Box>
                        <Text fontWeight="semibold">{entry.action}</Text>
                        <Text fontSize="sm" color="gray.500">{entry.admin_email}</Text>
                      </Box>
                      <Text fontSize="sm" color="gray.500">{new Date(entry.created_at).toLocaleString()}</Text>
                    </Flex>
                    {entry.metadata_json ? <Text mt={2} fontSize="sm">{entry.metadata_json}</Text> : null}
                    {entry.ip_address ? <Text mt={1} fontSize="xs" color="gray.500">IP: {entry.ip_address}</Text> : null}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Text color="gray.500">No audit entries yet.</Text>
            )}
          </InfoCard>
        </Stack>
      ) : null}
    </PlatformShell>
  )
}

function InfoCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
      <Heading size="sm" mb={3}>{title}</Heading>
      <Divider mb={3} />
      <Stack spacing={2}>{children}</Stack>
    </Box>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Flex justify="space-between" gap={3}>
      <Text color="gray.500">{label}</Text>
      <Text textAlign="right">{value}</Text>
    </Flex>
  )
}

function formatMaybeDate(value?: string | null) {
  if (!value) return "n/a"
  return new Date(value).toLocaleString()
}

function formatBytes(value: number) {
  if (!value) return "0 B"
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
